import { DetalhesPorta3D } from '../anatomia/DetalhesPorta3D'
import { vetor } from '../../dominio/geometria/vetor3'
import type { FaixaLongitudinal, PerfilVeiculo } from '../../dominio/didatico/perfilVeiculo'
import { Barra } from '../componentes/Barra'
import { MaterialPeca, MaterialVidro, Peca } from '../componentes/Peca'
import { FaceDeCorte, MaterialDeCorte } from './Seccionado'
import { poseSaidaDaPorta } from '../oficina/saidaDaPorta'
import { ABERTURA_RAD_PORTA, afastamentoDaChapa } from '../oficina/quadroDaPorta'
import { TONS_CARROCERIA } from '../../dominio/didatico/tokens'
import { contornoVidroPorta } from './formasCarroceria'
import { ESPESSURA_VIDRO_LATERAL, geometriaVidroLateral, xDoVidroNaCintura } from './vidrosLaterais'
import { xDaEstruturaNaAltura } from '../../dominio/didatico/perfilVeiculo'
import type { EstadoPortaOficina, IdVinculoPorta, OperacaoPorta } from '../../dominio/didatico/oficinaCorte'
import { MapeamentoEstrutural, useDeformacaoPeca, useGeometriaDeformada } from './contextoDeformacao'
import { baseChapaPorta } from './geometriaDeformacaoLateral'
import { CaixaArredondada } from '../componentes/CaixaArredondada'

// o giro e o deslocamento da chapa moram em `quadroDaPorta`: é de lá que os
// marcadores tiram a mesma pose, e dois números iguais em dois arquivos foi
// justamente o que deixou os marcadores para trás quando a porta se moveu
const ABERTURA_RAD = ABERTURA_RAD_PORTA
/** quanto a chapa externa se afasta para revelar o interior da porta */
const AFASTAMENTO_CHAPA = 0.4

interface Props {
  detalhesAnatomia?: boolean
  perfil: PerfilVeiculo
  id: string
  faixa: FaixaLongitudinal
  lado: 'esquerdo' | 'direito'
  estadoOficina?: EstadoPortaOficina
  /**
   * Quanto da saída da porta retirada já correu, de 0 a 1. Quem conta o tempo
   * é a aplicação, como no acionamento da ferramenta: a cena recebe o
   * progresso e não decide sozinha que a peça saiu.
   */
  progressoSaida?: number
  /** Prévia visual do efeito que será confirmado ao final da etapa. */
  progressoAcao?: number
  operacaoVisual?: OperacaoPorta
  /** Abertura angular direta da porta, de 0 (fechada) a 1 (totalmente aberta para extração). */
  abertura?: number
}

/**
 * Porta como grupo próprio.
 *
 * A dobradiça fica na borda dianteira: o grupo é posicionado no eixo da
 * dobradiça e as subpeças são modeladas em coordenadas locais negativas em Z.
 * Assim `aberta` é uma única rotação em Y, sem recalcular posição de peça.
 *
 * Afastar a chapa externa é o que revela barra de reforço, dobradiças e
 * limitador — o objetivo didático principal desta peça.
 */
export function PortaDidatica({
  perfil,
  detalhesAnatomia = false,
  id,
  faixa,
  lado,
  estadoOficina,
  progressoSaida = 1,
  progressoAcao = 0,
  operacaoVisual,
  abertura,
}: Props) {
  const sinal = lado === 'esquerdo' ? 1 : -1
  const x = sinal * (perfil.largura / 2 - 0.015)
  const comprimento = faixa.zDianteiro - faixa.zTraseiro
  const yBaixo = perfil.topoSoleiraY
  const yCintura = perfil.cinturaY
  const alturaChapa = yCintura - yBaixo
  const centroZ = -comprimento / 2
  const eDianteira = id.startsWith('portaDianteira')
  const contornoVidro = contornoVidroPorta(perfil, faixa, eDianteira)
  const xMoldura = (y: number) =>
    xDaEstruturaNaAltura(perfil, sinal, y) + sinal * (0.04 - ESPESSURA_VIDRO_LATERAL / 2) - x
  const pontoMoldura = (z: number, y: number) => vetor(xMoldura(y), y, z - faixa.zDianteiro)
  // Colisão lateral (Cinemática): a chapa externa é um clone subdividido que
  // passa pelo campo — intrusão central, vincos diagonais, barra de proteção
  // mais dura. Pose de repouso = porta fechada, que é quando o impacto ocorre.
  const deformacaoChapaOficina = sinal * afastamentoDaChapa(estadoOficina)
  const geometriaChapa = useGeometriaDeformada(baseChapaPorta(alturaChapa, comprimento - 0.03), 'porta', {
    posicao: [x + deformacaoChapaOficina, yBaixo + alturaChapa / 2, faixa.zDianteiro + centroZ],
  })
  const sufixoLado = lado === 'esquerdo' ? 'Esquerda' : 'Direita'
  const vidroColisao = useDeformacaoPeca(`vidro${eDianteira ? 'Dianteiro' : 'Traseiro'}${sufixoLado}`)?.vidro
  // o vidro acompanha a moldura (coluna e teto), com a porta fechada
  const geometriaVidro = useGeometriaDeformada(geometriaVidroLateral(perfil, sinal, contornoVidro), 'estrutura', {
    posicao: [xDoVidroNaCintura(perfil, sinal), 0, 0],
    rotacao: [0, -Math.PI / 2, 0],
  })
  const yMeioVidro = (contornoVidro.yBase + contornoVidro.yTopo) / 2
  const bordaFrente = [
    pontoMoldura(contornoVidro.frenteBase, contornoVidro.yBase),
    pontoMoldura((contornoVidro.frenteBase + contornoVidro.frenteTopo) / 2, yMeioVidro),
    pontoMoldura(contornoVidro.frenteTopo, contornoVidro.yTopo),
  ]
  const bordaTras = [
    pontoMoldura(contornoVidro.trasBase, contornoVidro.yBase),
    pontoMoldura((contornoVidro.trasBase + contornoVidro.trasTopo) / 2, yMeioVidro),
    pontoMoldura(contornoVidro.trasTopo, contornoVidro.yTopo),
  ]
  // A porta retirada ainda é desenhada enquanto sai de cena; só some quando a
  // saída termina. Antes ela desaparecia no mesmo quadro da retirada, que era
  // o único passo do exercício sem resposta visual.
  const saindo = estadoOficina?.removida === true
  if (saindo && progressoSaida >= 1) return null
  const saida = saindo ? poseSaidaDaPorta(progressoSaida, lado) : null
  // O vínculo cortado continua na cena, seccionado. Antes ele simplesmente
  // sumia: o aluno acionava a ferramenta e nada mudava à vista, porque a peça
  // que desapareceu já estava escondida atrás da chapa. Mostrar as duas partes
  // e o aço vivo do corte é o que prova o que a lâmina fez.
  //
  // As duas metades seguem dentro do grupo da porta, então acompanham a
  // abertura dela — simplificação aceita enquanto a porta só gira até ser
  // retirada; a metade presa à coluna ficaria para trás num modelo completo.
  const vinculoCortado = (vinculo: IdVinculoPorta) => estadoOficina?.vinculos[vinculo] === 'liberado'
  const aberturaTransitoria = operacaoVisual === 'criarAbertura' ? 0.12
    : operacaoVisual === 'amassarPorta' || operacaoVisual === 'amassarParalama' || operacaoVisual === 'comprimirMacaneta' ? 0.14
      : operacaoVisual === 'pincarPorta' ? 0.16
        : operacaoVisual === 'abrirPelaJanela' ? 0.18
          : operacaoVisual === 'desfolharPorta' ? 0.35
            : operacaoVisual === 'expandirTrava' ? 0.45 : 0
  const aberturaVisual = Math.max(estadoOficina?.abertura ?? 0, aberturaTransitoria * Math.max(0, Math.min(1, progressoAcao)))
  const fracaoAbertura = abertura ?? (estadoOficina ? aberturaVisual : 0)
  const anguloAberturaPorta = (sinal < 0 ? ABERTURA_RAD : -ABERTURA_RAD) * fracaoAbertura
  const deformacaoChapa = deformacaoChapaOficina
  const vidroVisivel = (!estadoOficina
    || estadoOficina.vidro === 'intacto'
    || estadoOficina.vidro === 'craqueladoComPelicula')
    && vidroColisao !== 'rompido'
  const expansaoEsquadria = estadoOficina?.esquadria === 'deformada' ? 1
    : operacaoVisual === 'abrirPelaJanela' ? Math.max(0, Math.min(1, progressoAcao)) : 0
  const aumentoEsquadria = 0.06 * expansaoEsquadria / (contornoVidro.yTopo - contornoVidro.yBase)
  const compressaoMacaneta = estadoOficina?.tecnicaCriacaoEspaco === 'compressaoMacaneta' ? 1
    : operacaoVisual === 'comprimirMacaneta' ? Math.max(0, Math.min(1, progressoAcao)) : 0
  const rotacaoChapa = estadoOficina?.tecnicaCriacaoEspaco === 'pincamento'
    ? sinal * 0.12
    : estadoOficina?.tecnicaCriacaoEspaco === 'amassamentoPorta'
      ? sinal * -0.08
      : estadoOficina?.chapa === 'desfolhada' ? sinal * 0.08 : 0

  return (
    // A saída soma na própria `Peca`: o giro precisa ser em torno da dobradiça,
    // que é a origem deste grupo. Num grupo externo ele giraria a porta em
    // torno do centro do veículo, e o braço de alavanca a jogaria longe.
    <Peca
      id={id}
      position={[
        x + (saida?.deslocamento[0] ?? 0),
        saida?.deslocamento[1] ?? 0,
        faixa.zDianteiro + (saida?.deslocamento[2] ?? 0),
      ]}
      aberturaRad={estadoOficina || abertura != null ? 0 : sinal < 0 ? ABERTURA_RAD : -ABERTURA_RAD}
      rotation={[0, anguloAberturaPorta + (saida?.giroY ?? 0), saida?.giroZ ?? 0]}
      deslocamentoAfastado={[sinal * 0.95, 0, 0]}
    >
      {/* moldura, barra de proteção, dobradiças e trava acompanham a chapa
          pelo mesmo campo — relativo à dobradiça, que é a origem deste grupo */}
      <MapeamentoEstrutural camada="porta" origem={[x, 0, faixa.zDianteiro]}>
      {/* chapa externa: some ou se afasta para expor o interior */}
      <Peca
        id={`${id}.chapaExterna`}
        deslocamentoAfastado={[sinal * AFASTAMENTO_CHAPA, 0, 0]}
        position={[deformacaoChapa, 0, 0]}
        rotation={[0, 0, rotacaoChapa]}
      >
        <mesh geometry={geometriaChapa} position={[0, yBaixo + alturaChapa / 2, centroZ]}>
          <MaterialPeca id={`${id}.chapaExterna`} corBase={TONS_CARROCERIA.porta} />
        </mesh>
        {/* maçaneta externa, logo abaixo da linha de cintura */}
        <mesh position={[sinal * (0.03 - compressaoMacaneta * 0.025), yCintura - 0.09, centroZ - comprimento * 0.28]}
          rotation={[compressaoMacaneta * 0.3, 0, 0]} scale={[1, 1 - compressaoMacaneta * 0.6, 1]}>
          <CaixaArredondada args={[0.03, 0.045, 0.14]} />
          <MaterialPeca
            id={`${id}.chapaExterna`}
            corBase={TONS_CARROCERIA.vidroMoldura}
            acabamento="metal"
          />
        </mesh>
      </Peca>

      {/* Batentes acompanham a borda arqueada do vidro, não a chapa: ficam numa
          `Peca` própria, sem afastamento, para a moldura não se soltar do vidro
          na vista explodida (a chapa externa se afasta 0,4 m). O id é o mesmo da
          chapa, então seleção, visibilidade e cor continuam as dela. */}
      <Peca id={`${id}.chapaExterna`} position={[deformacaoChapa, 0, 0]} rotation={[0, 0, rotacaoChapa]}>
      <group position={[0, -contornoVidro.yBase * aumentoEsquadria, 0]}
        scale={[1, 1 + aumentoEsquadria, 1]}>
        {[bordaFrente, bordaTras].flatMap((borda, ladoBorda) =>
          borda.slice(0, -1).map((de, i) => (
            <Barra key={`${ladoBorda}:${i}`} id={`${id}.chapaExterna`}
              de={de} para={borda[i + 1]} largura={0.03} espessura={0.045}
              corBase={TONS_CARROCERIA.esquadria} />
          )),
        )}
        <Barra id={`${id}.chapaExterna`} de={bordaFrente[2]} para={bordaTras[2]}
          largura={0.03} espessura={0.045} corBase={TONS_CARROCERIA.esquadria} />
      </group>
      </Peca>

      {vidroVisivel && <Peca id={`${id}.vidro`}>
        <mesh position={[xDoVidroNaCintura(perfil, sinal) - x, 0, -faixa.zDianteiro]}
          rotation={[0, -Math.PI / 2, 0]}
          geometry={geometriaVidro}>
          <MaterialVidro id={`${id}.vidro`} />
        </mesh>
      </Peca>}

      {/* Forro: o painel de acabamento que o ocupante vê por dentro. Removê-lo
          é o que expõe máquina de vidro, barra de reforço e fechadura — o
          mesmo gesto que a guarnição faz para alcançar o miolo da porta. */}
      <Peca id={`${id}.forro`} deslocamentoAfastado={[-sinal * 0.45, 0, 0]}>
        <mesh position={[-sinal * 0.055, yBaixo + alturaChapa / 2, centroZ]}>
          <boxGeometry args={[0.018, alturaChapa - 0.02, comprimento - 0.06]} />
          <MaterialPeca id={`${id}.forro`} />
        </mesh>

        {/* puxador interno, no forro */}
        <mesh position={[-sinal * 0.072, yCintura - 0.1, centroZ + comprimento * 0.26]}>
          <CaixaArredondada args={[0.03, 0.035, 0.12]} />
          <MaterialPeca id={`${id}.forro`} />
        </mesh>
        {detalhesAnatomia && <group position={[0, yBaixo + alturaChapa / 2, centroZ]}><DetalhesPorta3D id={id} parte="forro" sinal={sinal} comprimento={comprimento} altura={alturaChapa} /></group>}
      </Peca>

      {/* Máquina do vidro: trilho, braço e motor elétrico. É o que ocupa o
          miolo da porta junto com a barra, e o que sobra preso ao vidro quando
          se decide baixá-lo em vez de quebrá-lo. */}
      <Peca id={`${id}.maquinaVidro`} deslocamentoAfastado={[-sinal * 0.3, 0, 0]}>
        <Barra
          id={`${id}.maquinaVidro`}
          de={vetor(-sinal * 0.032, yBaixo + 0.05, centroZ)}
          para={vetor(-sinal * 0.032, yCintura - 0.02, centroZ)}
          largura={0.03}
          espessura={0.03}
        />
        <Barra
          id={`${id}.maquinaVidro`}
          de={vetor(-sinal * 0.032, yBaixo + 0.07, centroZ - comprimento * 0.24)}
          para={vetor(-sinal * 0.032, yCintura - 0.06, centroZ + comprimento * 0.24)}
          largura={0.026}
          espessura={0.026}
        />
        {detalhesAnatomia && <group position={[0, yBaixo + alturaChapa / 2, centroZ]}><DetalhesPorta3D id={id} parte="maquinaVidro" sinal={sinal} comprimento={comprimento} altura={alturaChapa} /></group>}
      </Peca>

      <Peca id={`${id}.motorVidro`} deslocamentoAfastado={[-sinal * 0.3, -0.1, 0]}>
        <mesh
          position={[-sinal * 0.04, yBaixo + 0.13, centroZ + 0.1]}
          rotation={[0, 0, Math.PI / 2]}
        >
          <cylinderGeometry args={[0.045, 0.045, 0.07, 12]} />
          <MaterialPeca id={`${id}.motorVidro`} metalico={0.5} rugosidade={0.45} />
        </mesh>
        {detalhesAnatomia && <group position={[-sinal * 0.04, yBaixo + 0.13, centroZ + 0.1]}><DetalhesPorta3D id={id} parte="motorVidro" sinal={sinal} comprimento={comprimento} altura={alturaChapa} /></group>}
      </Peca>

      <Peca id={`${id}.altoFalante`} deslocamentoAfastado={[-sinal * 0.35, 0, 0]}>
        <mesh position={[-sinal * 0.048, yBaixo + 0.1, centroZ - comprimento * 0.28]} rotation={[0, 0, Math.PI / 2]}>
          <cylinderGeometry args={[0.07, 0.07, 0.04, 14]} />
          <MaterialPeca id={`${id}.altoFalante`} />
        </mesh>
        {detalhesAnatomia && <group position={[-sinal * 0.048, yBaixo + 0.1, centroZ - comprimento * 0.28]}><DetalhesPorta3D id={id} parte="altoFalante" sinal={sinal} comprimento={comprimento} altura={alturaChapa} /></group>}
      </Peca>

      {/* barra de reforço lateral: diagonal dentro da porta */}
      <Peca id={`${id}.barraReforco`}>
        <Barra
          id={`${id}.barraReforco`}
          de={vetor(-sinal * 0.012, yBaixo + 0.12, -0.05)}
          para={vetor(-sinal * 0.012, yCintura - 0.12, centroZ * 2 + 0.05)}
          largura={0.075}
          espessura={0.075}
          cilindrica
        />
      </Peca>

      <Peca id={`${id}.dobradicaSuperior`}>
        <Dobradica
          id={`${id}.dobradicaSuperior`}
          sinal={sinal}
          y={yCintura - 0.14}
          cortada={vinculoCortado('dobradicaSuperior')}
        />
      </Peca>
      <Peca id={`${id}.dobradicaInferior`}>
        <Dobradica
          id={`${id}.dobradicaInferior`}
          sinal={sinal}
          y={yBaixo + 0.14}
          cortada={vinculoCortado('dobradicaInferior')}
        />
      </Peca>

      {/* Limitador: braço com curso, não caixinha. O aluno precisa ver que há
          um percurso que termina em batente — é ele que define até onde a porta
          abre sozinha e o que resiste quando ela é forçada além disso. */}
      <Peca id={`${id}.limitador`}>
        {/* corpo guia, preso à porta */}
        <mesh position={[-sinal * 0.02, (yBaixo + yCintura) / 2, -0.09]}>
          <CaixaArredondada args={[0.05, 0.06, 0.06]} />
          <MaterialPeca id={`${id}.limitador`} />
        </mesh>
        {/* braço correndo por dentro do guia até a carroceria, inteiro ou em
            dois tocos quando o corte já passou por ele */}
        {vinculoCortado('limitador') ? (
          <>
            <Barra
              id={`${id}.limitador`}
              de={vetor(-sinal * 0.02, (yBaixo + yCintura) / 2, -0.09)}
              para={vetor(-sinal * 0.02, (yBaixo + yCintura) / 2 + 0.009, -0.005)}
              largura={0.022}
              espessura={0.022}
            />
            <Barra
              id={`${id}.limitador`}
              de={vetor(-sinal * 0.02, (yBaixo + yCintura) / 2 + 0.013, 0.025)}
              para={vetor(-sinal * 0.02, (yBaixo + yCintura) / 2 + 0.02, 0.09)}
              largura={0.022}
              espessura={0.022}
            />
            <FaceDeCorte posicao={[-sinal * 0.02, (yBaixo + yCintura) / 2 + 0.009, -0.004]} />
            <FaceDeCorte posicao={[-sinal * 0.02, (yBaixo + yCintura) / 2 + 0.013, 0.024]} />
          </>
        ) : (
          <Barra
            id={`${id}.limitador`}
            de={vetor(-sinal * 0.02, (yBaixo + yCintura) / 2, -0.09)}
            para={vetor(-sinal * 0.02, (yBaixo + yCintura) / 2 + 0.02, 0.09)}
            largura={0.022}
            espessura={0.022}
          />
        )}
        {/* batente na ponta do braço */}
        <mesh position={[-sinal * 0.02, (yBaixo + yCintura) / 2 + 0.02, 0.1]}>
          <CaixaArredondada args={[0.035, 0.05, 0.03]} />
          <MaterialPeca id={`${id}.limitador`} metalico={0.5} rugosidade={0.45} />
        </mesh>
      </Peca>

      {/* fechadura/trava: borda traseira da porta */}
      <Peca id={`${id}.fechadura`}>
        {vinculoCortado('trava') ? (
          [1, -1].map((metade) => (
            <group
              key={metade}
              position={[
                -sinal * 0.015,
                (yBaixo + yCintura) / 2 - 0.04 + metade * 0.042,
                centroZ * 2 + 0.06,
              ]}
            >
              <mesh>
                <CaixaArredondada args={[0.05, 0.048, 0.08]} />
                <MaterialPeca id={`${id}.fechadura`} />
              </mesh>
              <mesh position={[0, -metade * 0.024, 0]}>
                <CaixaArredondada args={[0.051, 0.006, 0.081]} />
                <MaterialDeCorte />
              </mesh>
            </group>
          ))
        ) : (
          <mesh position={[-sinal * 0.015, (yBaixo + yCintura) / 2 - 0.04, centroZ * 2 + 0.06]}>
            <CaixaArredondada args={[0.05, 0.13, 0.08]} />
            <MaterialPeca id={`${id}.fechadura`} />
          </mesh>
        )}
      </Peca>

      <Peca id={`${id}.fiacao`}>
        {vinculoCortado('fiacao') ? (
          <>
            <Barra
              id={`${id}.fiacao`}
              de={vetor(-sinal * 0.035, meioAltura(yBaixo, yCintura) - 0.1, 0.03)}
              para={vetor(-sinal * 0.035, meioAltura(yBaixo, yCintura) - 0.1, -0.04)}
              largura={0.018}
              espessura={0.018}
              cilindrica
            />
            <Barra
              id={`${id}.fiacao`}
              de={vetor(-sinal * 0.035, meioAltura(yBaixo, yCintura) - 0.094, -0.07)}
              para={vetor(-sinal * 0.035, meioAltura(yBaixo, yCintura) - 0.094, -0.14)}
              largura={0.018}
              espessura={0.018}
              cilindrica
            />
            <FaceDeCorte posicao={[-sinal * 0.035, meioAltura(yBaixo, yCintura) - 0.1, -0.039]} raio={0.012} />
            <FaceDeCorte posicao={[-sinal * 0.035, meioAltura(yBaixo, yCintura) - 0.094, -0.071]} raio={0.012} />
          </>
        ) : (
          <Barra
            id={`${id}.fiacao`}
            de={vetor(-sinal * 0.035, meioAltura(yBaixo, yCintura) - 0.1, 0.03)}
            para={vetor(-sinal * 0.035, meioAltura(yBaixo, yCintura) - 0.1, -0.14)}
            largura={0.018}
            espessura={0.018}
            cilindrica
          />
        )}
      </Peca>
      </MapeamentoEstrutural>
    </Peca>
  )
}

const meioAltura = (baixo: number, alto: number) => (baixo + alto) / 2

/**
 * Dobradiça com as duas folhas.
 *
 * Antes era um cilindro solto: o pino existia, mas não havia nada prendendo-o
 * de um lado nem do outro. Sem as duas folhas o aluno não vê que a dobradiça é
 * o que liga a porta à coluna, e portanto não vê por que ela é alvo de corte.
 */
function Dobradica({
  id,
  sinal,
  y,
  cortada = false,
}: {
  id: string
  sinal: number
  y: number
  cortada?: boolean
}) {
  return (
    <group>
      {/* pino, sobre o próprio eixo de giro — partido ao meio depois do corte */}
      {cortada ? (
        [1, -1].map((metade) => (
          <group key={metade} position={[-sinal * 0.02, y + metade * 0.039, -0.02]}>
            <mesh>
              <cylinderGeometry args={[0.026, 0.026, 0.05, 12]} />
              <MaterialPeca id={id} metalico={0.5} rugosidade={0.4} />
            </mesh>
            <mesh position={[0, -metade * 0.025, 0]}>
              <cylinderGeometry args={[0.0265, 0.0265, 0.006, 12]} />
              <MaterialDeCorte />
            </mesh>
          </group>
        ))
      ) : (
        <mesh position={[-sinal * 0.02, y, -0.02]}>
          <cylinderGeometry args={[0.026, 0.026, 0.11, 12]} />
          <MaterialPeca id={id} metalico={0.5} rugosidade={0.4} />
        </mesh>
      )}
      {/* folha da porta, indo para trás na chapa */}
      <mesh position={[-sinal * 0.02, y, -0.07]}>
        <CaixaArredondada args={[0.02, 0.07, 0.09]} />
        <MaterialPeca id={id} metalico={0.5} rugosidade={0.4} />
      </mesh>
      {/* folha da carroceria, indo para a frente em direção à coluna */}
      <mesh position={[-sinal * 0.02, y, 0.05]}>
        <CaixaArredondada args={[0.02, 0.07, 0.09]} />
        <MaterialPeca id={id} metalico={0.5} rugosidade={0.4} />
      </mesh>
    </group>
  )
}

